import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@ui/card.tsx';
import { SegmentList } from '@admin/components/segments/segment-list.tsx';
import { CreateSegmentDialog } from '@admin/components/segments/create-segment-dialog.tsx';
import { UpdateSegmentDialog } from '@admin/components/segments/update-segment-dialog.tsx';
import { SegmentPreviewDialog } from '@admin/components/segments/segment-preview-dialog.tsx';
import { getApiAdminsegmentsGetCollectionQueryKey } from '@r/api/generated/client.ts';
import type { SegmentSegmentCollectionReadModel } from '@r/api/generated/client.schemas.ts';

type Segment = SegmentSegmentCollectionReadModel;

/** Page principale de gestion des segments : liste, création, modification et prévisualisation. */
export default function Segments() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [previewSegment, setPreviewSegment] = useState<Segment | null>(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

  const handlePreview = (segment: Segment) => {
    setPreviewSegment(segment);
    setIsPreviewDialogOpen(true);
  };

  const handleEditDetails = (segment: Segment) => {
    setEditingSegment(segment);
    setIsEditDialogOpen(true);
  };

  const handleManageRules = (segment: Segment) => {
    navigate({ to: '/settings/segments/$id', params: { id: String(segment.id) } });
  };

  const handleCreated = (segment: { id: number }) => {
    queryClient.invalidateQueries({ queryKey: getApiAdminsegmentsGetCollectionQueryKey() });
    navigate({ to: '/settings/segments/$id', params: { id: String(segment.id) } });
  };

  return (
    <div className="space-y-10">
      <Card>
        <CardContent className="pt-6 space-y-2.5">
          <div className="flex items-center justify-between">
            <h2 className="text-4xl font-bold">{t('admin.segments.page.title')}</h2>
            <CreateSegmentDialog onCreated={handleCreated} />
          </div>

          <div className="w-2/3 space-y-2.5 pb-7">
            <p className="text-muted-foreground">{t('admin.segments.description')}</p>
          </div>

          <SegmentList
            onPreview={handlePreview}
            onEditDetails={handleEditDetails}
            onManageRules={handleManageRules}
            refreshKey={refreshKey}
            onDeleted={() => setRefreshKey((key) => key + 1)}
          />
        </CardContent>
      </Card>

      <UpdateSegmentDialog
        segment={editingSegment}
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={() => setRefreshKey((key) => key + 1)}
      />

      <SegmentPreviewDialog
        segment={previewSegment}
        isOpen={isPreviewDialogOpen}
        onOpenChange={setIsPreviewDialogOpen}
        onEditDetails={handleEditDetails}
        onManageRules={handleManageRules}
        onDeleted={() => setRefreshKey((key) => key + 1)}
      />
    </div>
  );
}
